import { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { meetingsAPI } from '../../lib/api';
import { X, Plus } from 'lucide-react';

const EMPTY_NEW = { name: '', designation: '', mobile: '', email: '', wechat_id: '', is_decision_maker: false };

// `value` is the raw list this component maintains for NEW additions this session:
// [{id,name}] for picked existing contacts, [{name,...}] for freshly typed ones. Parent
// sends this straight through as the API's `contacts` field (create / PUT / submit all
// accept the same shape). `attachedIds` are contacts already recorded on the meeting —
// shown as read-only so people don't think they still need to be picked.
export default function ContactsEditor({ value, onChange, companyType, companyRef, meetingId, attachedIds = [], disabled = false }) {
  const [existing, setExisting] = useState([]);
  const [newEntry, setNewEntry] = useState(null);

  useEffect(() => {
    if (!companyType || !companyRef) { setExisting([]); return; }
    meetingsAPI.companyContacts(companyType, companyRef, meetingId).then((r) => setExisting(r.data.contacts || [])).catch(() => setExisting([]));
  }, [companyType, companyRef, meetingId]);

  const toggleExisting = (c) => {
    const picked = value.some((v) => v.id === c.id);
    onChange(picked ? value.filter((v) => v.id !== c.id) : [...value, { id: c.id, name: c.name }]);
  };

  const addNew = () => {
    if (!newEntry.name.trim()) return;
    onChange([...value, { ...newEntry }]);
    setNewEntry(null);
  };

  const removeNew = (idx) => onChange(value.filter((_, i) => i !== idx));
  const attached = existing.filter((c) => attachedIds.includes(c.id));
  const pickable = existing.filter((c) => !attachedIds.includes(c.id));

  return (
    <div className="space-y-2" data-testid="contacts-editor">
      <Label className="block">People met</Label>
      {attached.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="contacts-already-recorded">
          {attached.map((c) => (
            <Badge key={c.id} variant="outline" className="text-xs">✓ {c.name} (recorded)</Badge>
          ))}
        </div>
      )}
      {pickable.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {pickable.map((c) => (
            <label key={c.id} className={`flex items-center gap-1.5 border rounded-sm px-2 py-1 text-sm ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
              data-testid={`contact-existing-${c.id}`}>
              <Checkbox disabled={disabled} checked={value.some((v) => v.id === c.id)} onCheckedChange={() => toggleExisting(c)} />
              {c.name}{c.is_decision_maker && <Badge variant="outline" className="text-[9px] ml-1">DM</Badge>}
            </label>
          ))}
        </div>
      )}
      {value.filter((v) => !v.id).map((v, i) => (
        <div key={i} className="flex items-center gap-2 text-sm border rounded-sm px-2 py-1" data-testid={`contact-new-${i}`}>
          <span className="flex-1">{v.name}{v.designation ? ` — ${v.designation}` : ''}</span>
          {!disabled && (
            <button type="button" onClick={() => removeNew(value.findIndex((x) => x === v))} data-testid={`contact-new-remove-${i}`}>
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
        </div>
      ))}
      {disabled ? null : newEntry === null ? (
        <Button type="button" size="sm" variant="outline" className="rounded-sm" data-testid="contact-add-new-btn"
          onClick={() => setNewEntry({ ...EMPTY_NEW })}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add a person
        </Button>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border rounded-sm p-2" data-testid="contact-new-form">
          <Input placeholder="Name *" value={newEntry.name} onChange={(e) => setNewEntry({ ...newEntry, name: e.target.value })} data-testid="contact-new-name" />
          <Input placeholder="Designation" value={newEntry.designation} onChange={(e) => setNewEntry({ ...newEntry, designation: e.target.value })} data-testid="contact-new-designation" />
          <Input placeholder="Mobile" value={newEntry.mobile} onChange={(e) => setNewEntry({ ...newEntry, mobile: e.target.value })} data-testid="contact-new-mobile" />
          <Input placeholder="Email" value={newEntry.email} onChange={(e) => setNewEntry({ ...newEntry, email: e.target.value })} data-testid="contact-new-email" />
          <Input placeholder="WeChat ID" value={newEntry.wechat_id} onChange={(e) => setNewEntry({ ...newEntry, wechat_id: e.target.value })} data-testid="contact-new-wechat" />
          <label className="flex items-center gap-1.5 text-sm"><Checkbox checked={newEntry.is_decision_maker}
            onCheckedChange={(v) => setNewEntry({ ...newEntry, is_decision_maker: !!v })} /> Decision maker</label>
          <div className="col-span-1 sm:col-span-2 flex gap-2">
            <Button type="button" size="sm" onClick={addNew} data-testid="contact-new-save-btn">Add</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setNewEntry(null)} data-testid="contact-new-cancel-btn">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
